import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { VideoPlayer } from '@/components/VideoPlayer';
import { Button } from '@/components/ui/Button';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { FullSource } from '@/types/source';
import { roundToTwoDecimals } from '@/utils/time';
import { BboxSourceSelectionLayer } from './BboxSourceSelectionLayer';
import type { OverlayRect } from '@/utils/bboxToSourceRect';
import { bboxOverlayToSourcePixels } from '@/utils/bboxToSourceRect';
import { findVideoInPlayerWrapper } from '@/utils/findVideoInPlayerWrapper';

export type ReportEventFrameSelectStepHandle = {
    confirm: () => void;
};

interface ReportEventFrameSelectStepProps {
    source: FullSource;
    eventStartSeconds: number;
    onConfirm: (payload: {
        time: number;
        bbox: [number, number, number, number];
    }) => void;
    onSubmitDisabledChange: (disabled: boolean) => void;
}

export const ReportEventFrameSelectStep = forwardRef<
    ReportEventFrameSelectStepHandle,
    ReportEventFrameSelectStepProps
>(function ReportEventFrameSelectStep(
    { source, eventStartSeconds, onConfirm, onSubmitDisabledChange },
    ref,
) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const currentTimeRef = useRef(eventStartSeconds);
    const attachedVideoRef = useRef<HTMLVideoElement | null>(null);
    const [overlayRect, setOverlayRect] = useState<OverlayRect | null>(null);
    const [videoLayoutRect, setVideoLayoutRect] = useState<OverlayRect | null>(
        null,
    );
    const [isPaused, setIsPaused] = useState(false);

    const handleOverlayRectChange = useCallback((rect: OverlayRect) => {
        setOverlayRect(rect);
    }, []);

    const handleVideoRectChange = useCallback((rect: OverlayRect) => {
        setVideoLayoutRect(rect);
    }, []);

    const handleTimeUpdate = useCallback((time: number) => {
        currentTimeRef.current = time;
    }, []);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }

        let detachListeners: (() => void) | undefined;
        let attached: HTMLVideoElement | null = null;

        const attachIfNeeded = () => {
            if (attached && wrapper.contains(attached)) {
                return;
            }
            detachListeners?.();
            detachListeners = undefined;
            attachedVideoRef.current = null;
            attached = null;

            const video = findVideoInPlayerWrapper(wrapper);
            if (!video) {
                return;
            }

            attached = video;
            attachedVideoRef.current = video;
            setIsPaused(video.paused);

            const onPlay = () => setIsPaused(false);
            const onPause = () => setIsPaused(true);
            video.addEventListener('play', onPlay);
            video.addEventListener('pause', onPause);
            detachListeners = () => {
                video.removeEventListener('play', onPlay);
                video.removeEventListener('pause', onPause);
                attachedVideoRef.current = null;
            };
        };

        attachIfNeeded();

        const mutationObserver = new MutationObserver(() => attachIfNeeded());
        mutationObserver.observe(wrapper, { subtree: true, childList: true });

        const burstId = window.setInterval(attachIfNeeded, 150);
        const burstStop = window.setTimeout(() => {
            window.clearInterval(burstId);
        }, 4000);

        return () => {
            mutationObserver.disconnect();
            window.clearInterval(burstId);
            window.clearTimeout(burstStop);
            detachListeners?.();
            attachedVideoRef.current = null;
        };
    }, [source.src]);

    const handleConfirm = useCallback(() => {
        const wrapper = wrapperRef.current;
        const video =
            attachedVideoRef.current ?? findVideoInPlayerWrapper(wrapper);
        const sourceWidth =
            source.width > 0 ? source.width : (video?.videoWidth ?? 1);
        const sourceHeight =
            source.height > 0 ? source.height : (video?.videoHeight ?? 1);
        if (!isPaused || !overlayRect || !videoLayoutRect) {
            return;
        }
        const bbox = bboxOverlayToSourcePixels(
            overlayRect,
            videoLayoutRect,
            sourceWidth,
            sourceHeight,
        );
        onConfirm({
            time: roundToTwoDecimals(currentTimeRef.current),
            bbox,
        });
    }, [
        isPaused,
        onConfirm,
        overlayRect,
        source.height,
        source.width,
        videoLayoutRect,
    ]);

    useImperativeHandle(ref, () => ({
        confirm: handleConfirm,
    }));

    const confirmDisabled =
        !isPaused ||
        !overlayRect ||
        !videoLayoutRect ||
        videoLayoutRect.width < 4 ||
        videoLayoutRect.height < 4;

    useEffect(() => {
        onSubmitDisabledChange(confirmDisabled);
    }, [confirmDisabled, onSubmitDisabledChange]);

    const videoSrc = source.src || '';

    const handleSelectThisFrame = () => {
        const video =
            attachedVideoRef.current ??
            findVideoInPlayerWrapper(wrapperRef.current);
        video?.pause();
    };

    return (
        <div className="flex flex-col gap-4 p-6">
            <div ref={wrapperRef} className="relative w-full">
                <VideoPlayer
                    src={videoSrc}
                    source={source}
                    controls
                    startTime={eventStartSeconds}
                    onTimeUpdate={handleTimeUpdate}
                />
                <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    className="absolute left-3 top-3 z-[29] bg-grey-500 text-white-100 hover:bg-grey-600"
                    onClick={handleSelectThisFrame}
                >
                    {t('eventEditor.selectThisFrame')}
                </Button>
                <BboxSourceSelectionLayer
                    containerRef={wrapperRef}
                    selectionVisible={isPaused}
                    onOverlayRectChange={handleOverlayRectChange}
                    onVideoRectChange={handleVideoRectChange}
                />
            </div>
        </div>
    );
});
